import { useEffect, useState } from 'react';

import Dialog from 'components/Dialog';
import ToggleButtonGroup from 'components/ToggleButtonGroup';
import ToggleButton from 'components/ToggleButtonGroup/ToggleButton';
import UserAvatar from 'components/UserAvatar';
import { Button } from 'components/UtilElements';
import { getRawUser } from 'utils';

import { ReactComponent as InfoIcon } from 'assets/info-icon.svg';

import MembersRolesAndPermissionsDialog from './MembersRolesAndPermissionsDialog';

export interface CourseTeamRolesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  course: CourseDetails | null;
  onChangeAdmin: () => void;
  onSave: (instructors: RawUser[], tas: RawUser[]) => Promise<void>;
  isSaving: boolean;
}

enum Role {
  INSTRUCTOR = 'instructor',
  TA = 'ta',
}

const teamUserToUser = (teamUser: RawUser, role: Role): RawUser & { role: Role } => {
  return {
    userId: teamUser.userId,
    name: teamUser.name,
    emailId: teamUser.emailId,
    role,
  };
};

const CourseTeamRolesDialog = ({
  isOpen,
  onClose,
  course,
  onChangeAdmin,
  onSave,
  isSaving,
}: CourseTeamRolesDialogProps) => {
  const [courseTeam, setCourseTeam] = useState<(RawUser & { role: Role })[]>([]);
  const [isMembersRoleAndPermissionsDialogOpen, setIsMembersRoleAndPermissionsDialogOpen] = useState(false);

  const handleCloseMembersRoleAndPermissionsDialog = () => {
    setIsMembersRoleAndPermissionsDialogOpen(false);
  };

  const handleClickInfoIcon = () => {
    setIsMembersRoleAndPermissionsDialogOpen(true);
  };

  const handleChangeRole = (userId: string) => (values: string[]) => {
    if (values.length === 0) return;
    setCourseTeam((prev) =>
      prev.map((user) => (user.userId === userId ? { ...user, role: values[0] as Role } : user))
    );
  };

  const handleSave = () => {
    const { instructors, tas } = courseTeam.reduce<{ instructors: RawUser[]; tas: RawUser[] }>(
      (acc, courseUser) => {
        const user = getRawUser(courseUser);
        if (courseUser.role === Role.INSTRUCTOR) {
          acc.instructors.push(user);
        } else if (courseUser.role === Role.TA) {
          acc.tas.push(user);
        }
        return acc;
      },
      { instructors: [], tas: [] }
    );

    onSave(instructors, tas);
  };

  useEffect(() => {
    if (!isOpen) return;
    if (!course?.team) return;
    setCourseTeam([
      ...course.team.instructors
        .filter((instructor) => instructor.valid !== 0)
        .map((instructor) => teamUserToUser(instructor, Role.INSTRUCTOR)),
      ...course.team.tas
        .filter((instructor) => instructor.valid !== 0)
        .map((ta) => teamUserToUser(ta, Role.TA)),
    ]);
  }, [isOpen, course?.team]);

  if (!course) return null;
  if (!course.team.isAdminSet) return null;

  return (
    <>
      <Dialog
        variant='medium'
        isOpen={isOpen}
        onClose={onClose}
        className='course-team-roles-dialog'
        classes={{
          body: 'course-team-roles-dialog__body',
          footer: 'course-team-roles-dialog__footer',
        }}
        title='Finalise course team roles'
        footer={
          <>
            <Button variant='outlined' color='warning' size='large' onClick={onChangeAdmin}>
              Change Admin
            </Button>
            <Button
              variant='contained'
              color='success'
              size='large'
              onClick={handleSave}
              isLoading={isSaving}
            >
              Save and Continue
            </Button>
          </>
        }
      >
        <div className='note'>
          <div>Understand capabilities of each role with </div>{' '}
          <InfoIcon className='note__icon' onClick={handleClickInfoIcon} /> <div>button</div>
        </div>
        <UserAvatar className='import-course__gutters' {...course.team.admin} hasActions>
          <div className='tag tag--grey'>Admin</div>
        </UserAvatar>
        {courseTeam.map((courseUser) => (
          <UserAvatar key={courseUser.userId} className='import-course__gutters' {...courseUser} hasActions>
            <div className='course-team-roles-dialog__body__actions'>
              <ToggleButtonGroup value={[courseUser.role]} onChange={handleChangeRole(courseUser.userId)}>
                <ToggleButton value={Role.INSTRUCTOR}>Instructor</ToggleButton>
                <ToggleButton value={Role.TA}>TA</ToggleButton>
              </ToggleButtonGroup>
            </div>
          </UserAvatar>
        ))}
      </Dialog>
      <MembersRolesAndPermissionsDialog
        open={isMembersRoleAndPermissionsDialogOpen}
        onClose={handleCloseMembersRoleAndPermissionsDialog}
      />
    </>
  );
};

export default CourseTeamRolesDialog;
